import React, { useCallback, useId, useMemo, useState } from 'react';
import date from 'date-and-time';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons/faCalendarDays';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../../common/types';
import { SMLUpgradeModal } from '../SMLUpgradeModal/SMLUpgradeModal';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { useShareTokenContext } from '../../contexts/ShareTokenContext';
import { formatUtcDay, latestCompleteUtcDay, oldestAllowedUtcDay } from '../../utils/utcDataDay';
import styles from './SMLDatePicker.module.scss';
import { datePickerFormat, datePickerParamName, rangeDaysParamName, rangeDaysOptions } from './consts';

interface SMLDatePickerProps {
  placeholder?: string;
  showRangePresets?: boolean;
}

const SMLDatePickerComponent: React.FC<SMLDatePickerProps> = ({
  placeholder = 'Select date',
  showRangePresets = false,
}) => {
  const intl = useIntl();
  const smlRouter = useSMLRouter();
  const { selectedDateValue, rangeDays } = useSMLDatePickerDateParam();
  const userTokenDecoded = useUserTokenDecoded();
  const { isPublicMode } = useShareTokenContext();
  const generatedId = useId();
  // UncontrolledTooltip target needs a CSS-compliant id (no leading colons from useId in some envs)
  const tooltipTargetId = `sml-datepicker-${generatedId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  // The range they clicked, so the offer is a plan that actually reaches it.
  const [lockedRangeDays, setLockedRangeDays] = useState<number | undefined>(undefined);

  // The data is daily in UTC, and musicapi counts both bounds from UTC today.
  const maxDate = formatUtcDay(latestCompleteUtcDay());
  const historyDaysLimit = userTokenDecoded?.licenseInfo?.smlCapabilities.historyDaysLimit;

  // Calculate minimum date based on user's history days limit
  const minDate = useMemo(
    () => formatUtcDay(oldestAllowedUtcDay(new Date(), historyDaysLimit ?? 7)),
    [historyDaysLimit]
  );

  // A range the plan does not reach is a lock, not a dead chip: it says so and sells the plan that
  // reaches it. The entitlement has to be known first — a missing token is not a locked range.
  const isRangeLocked = useCallback(
    (days: number) => !isPublicMode && historyDaysLimit !== undefined && days > historyDaysLimit,
    [isPublicMode, historyDaysLimit]
  );

  const openUpgradeModal = useCallback((days: number) => () => setLockedRangeDays(days), []);
  const closeUpgradeModal = useCallback(() => setLockedRangeDays(undefined), []);
  const handleDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const parsedDate = date.parse(value, datePickerFormat);
      if (Number.isNaN(parsedDate.getTime())) {
        smlRouter.setParams({
          [datePickerParamName]: undefined,
        } as any);
      } else {
        smlRouter.setParams({
          [datePickerParamName]: date.format(parsedDate, datePickerFormat),
        } as any);
      }
    },
    [smlRouter]
  );

  const handleRangeDaysChange = useCallback(
    (days: number) => {
      smlRouter.setParams({
        [rangeDaysParamName]: String(days),
      } as any);
    },
    [smlRouter]
  );

  const tooltipMessage = intl.formatMessage({
    id: 'app.sml.publicMode.datePicker.tooltip',
    defaultMessage: 'Sign up to explore other dates',
  });

  return (
    <div className={styles.datePickerContainer} id={tooltipTargetId}>
      {showRangePresets && (
        <div className={styles.rangePresets}>
          {rangeDaysOptions.map((days) => {
            const locked = isRangeLocked(days);
            return (
              <button
                key={days}
                type="button"
                className={clsx(
                  styles.rangeButton,
                  rangeDays === days && styles.rangeButtonActive,
                  locked && styles.rangeButtonLocked
                )}
                onClick={locked ? openUpgradeModal(days) : () => handleRangeDaysChange(days)}
                disabled={isPublicMode}
              >
                {locked && <FontAwesomeIcon icon={faLock} className={styles.rangeLockIcon} />}
                {intl.formatMessage(
                  {
                    id: 'app.sml.datepicker.range.days',
                    defaultMessage: '{days}d',
                  },
                  { days }
                )}
              </button>
            );
          })}
        </div>
      )}
      <div className={styles.datePickerWrapper}>
        <FontAwesomeIcon icon={faCalendarDays} className={styles.dateIcon} />
        <input
          type="date"
          value={selectedDateValue}
          onChange={handleDateChange}
          className={styles.dateInput}
          placeholder={placeholder}
          min={minDate}
          max={maxDate}
          disabled={isPublicMode}
        />
      </div>
      {isPublicMode && (
        <UncontrolledTooltip target={tooltipTargetId} placement="bottom">
          {tooltipMessage}
        </UncontrolledTooltip>
      )}
      <SMLUpgradeModal
        isOpen={lockedRangeDays !== undefined}
        onClose={closeUpgradeModal}
        feature={SMLUpgradeFeature.history}
        requiredHistoryDays={lockedRangeDays}
      />
    </div>
  );
};

export const SMLDatePicker = TypedMemo(SMLDatePickerComponent);
